import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";
import { clearWorkspacePermsCache } from "./useWorkspacePerms";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [impersonation, setImpersonation] = useState(null);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(
    localStorage.getItem("co_ws"),
  );
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("co_token");
    if (!token) {
      setUser(null);
      setMemberships([]);
      setImpersonation(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setMemberships(data.memberships || []);
      // Iter 253n · Server-authoritative impersonation state. Prefer the
      // JWT-derived `impersonation` object from /auth/me over the
      // sessionStorage flag set by the initiator — that flag can be
      // cleared/spoofed on the client, but the server payload cannot.
      setImpersonation(data.impersonation || null);
      // restore or set active workspace
      const stored = localStorage.getItem("co_ws");
      if (stored && data.memberships?.some((m) => m.workspace_id === stored)) {
        setActiveWorkspaceId(stored);
      } else if (data.memberships?.length > 0) {
        const wid = data.memberships[0].workspace_id;
        localStorage.setItem("co_ws", wid);
        setActiveWorkspaceId(wid);
      } else {
        setActiveWorkspaceId(null);
        localStorage.removeItem("co_ws");
      }
    } catch (e) {
      localStorage.removeItem("co_token");
      setUser(null);
      setImpersonation(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("co_token", data.token);
    await refresh();
    return data.user;
  };

  const signup = async (payload, opts = {}) => {
    const { data } = await api.post("/auth/signup", payload);
    if (!data.token) {
      // Waitlisted — caller (Signup.jsx) handles the thank-you screen.
      if (opts.allowWaitlist) return data;
      throw new Error(data.message || "Account pending approval");
    }
    localStorage.setItem("co_token", data.token);
    await refresh();
    return { ...data, user: data.user };
  };

  const logout = () => {
    // R159e · Feb 2026 — Preserve the last workspace + last page on
    // manual logout so the next login lands on exactly the same
    // workspace and screen the user was working on. `co_ws` and
    // `acq_last_page` intentionally survive — they're only used when
    // there's a valid token and matching membership on the next login.
    localStorage.removeItem("co_token");
    setUser(null);
    setMemberships([]);
    // Keep `activeWorkspaceId` in memory so a stale redirect during the
    // logout tick doesn't blow up; the reload below flushes it anyway.
    window.location.href = "/";
  };

  const switchWorkspace = (wid) => {
    // Iter 253j · Only permit switching to a workspace we already have an
    // active membership in. Prevents URL-hack switching (`localStorage.co_ws
    // = someOtherWsId`) during impersonation or after a role revoke.
    if (!memberships.some((m) => m.workspace_id === wid)) {
      return;
    }
    // Invalidate the perms cache so limited roles get their own perms on
    // arrival — not the admin perms carried over from the previous ws.
    try { clearWorkspacePermsCache(); } catch { /* ignore */ }
    localStorage.setItem("co_ws", wid);
    setActiveWorkspaceId(wid);
  };

  const activeWorkspace =
    memberships.find((m) => m.workspace_id === activeWorkspaceId)?.workspace || null;

  // Feb 2026 R100 · Prime the workspace-timezone cache so attendance
  // displays render times in the ws tz (IST etc.) instead of UTC or the
  // browser's local tz. See src/lib/wsTime.js.
  useEffect(() => {
    if (activeWorkspace?.timezone) {
      import("@/lib/wsTime").then((m) => m.setWorkspaceTimezone(activeWorkspace.timezone))
        .catch(() => {});
    }
  }, [activeWorkspace?.timezone]);

  return (
    <AuthCtx.Provider
      value={{
        user,
        memberships,
        impersonation,
        activeWorkspaceId,
        activeWorkspace,
        loading,
        login,
        signup,
        logout,
        refresh,
        switchWorkspace,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
